import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography } from '@mui/material';
import { formatPrice } from '../../utils/currencyUtils';
import { holdReasonKeys, payoutDescriptor } from '../../utils/storePayout';

const formatDay = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

/**
 * When a STORE order's money reaches the store: a chip ("Awaiting delivery" / "Payout on <date>" /
 * "Paid out <date>" / "On hold" / "Cancelled" / "Refunded in full"), and with `detailed` also the reason for a hold,
 * what the store receives and a one-line explanation. Renders nothing for SERVICE orders and for
 * store orders without a payout block (paid before held payouts) so those rows look as before.
 */
export default function StorePayoutStatus({ order, detailed = false }) {
  const { t } = useTranslation();
  const info = payoutDescriptor(order);
  if (!info) return null;

  const { payout } = info;
  const reasons = info.key === 'ON_HOLD' ? holdReasonKeys(payout) : [];
  const reasonText = reasons.length > 0
    ? t('payouts.holdReasons', { reasons: reasons.map((r) => t(`payouts.hold${r}`)).join(', ') })
    : null;
  const showAmount = detailed && payout.amount != null && info.key !== 'CANCELLED' && info.key !== 'REFUNDED_IN_FULL' && info.key !== 'REFUNDED_AFTER_RELEASE';

  return (
    <Box data-testid={`payout-status-${order.id}`} sx={{ mt: 0.5 }}>
      <Chip
        label={t(`payouts.status${info.key}`, { date: formatDay(info.date) })}
        color={info.color}
        size="small"
        variant="outlined"
      />
      {reasonText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {reasonText}
        </Typography>
      )}
      {!detailed && info.hintKey.startsWith('RELEASED_EARLY') && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {t(`payouts.hint${info.hintKey}`)}
        </Typography>
      )}
      {detailed && (
        <>
          {showAmount && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('payouts.amount', {
                amount: formatPrice(payout.amount, payout.currencyCode || order.currencyCode),
              })}
              <Typography component="span" variant="caption" color="text.secondary">
                {` (${t('payouts.amountNote')})`}
              </Typography>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(`payouts.hint${info.hintKey}`)}
          </Typography>
        </>
      )}
    </Box>
  );
}
